import { useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useToastContext } from '@/contexts/toastContext';
import financePayrollService, { BulkReport, CandidateTransaction } from '@/services/financePayroll';
import { FetchApiIds } from '@/types';

type Result = {
  candidates: CandidateTransaction[];
  loadingCandidates: boolean;
  loadCandidates: () => Promise<void>;
  linkTransaction: (transactionId: string) => Promise<void>;
  unlinkTransaction: (transactionId: string) => Promise<void>;
};

/**
 * Reconciling a payroll report against the bank transactions that paid it.
 * Extracted from the container so the page stays presentational.
 */
export default function usePayrollTransactionLinking(
  reportId: string,
  report: BulkReport | null
): Result {
  const queryClient = useQueryClient();
  const toast = useToastContext();
  const [candidates, setCandidates] = useState<CandidateTransaction[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);

  const loadCandidates = useCallback(async () => {
    if (!report) return;
    setLoadingCandidates(true);
    try {
      setCandidates(
        await financePayrollService.getCandidateTransactions(
          report.vendorId,
          report.periodStart,
          report.periodEnd
        )
      );
    } catch {
      toast.showErrorFeedback('Failed to load candidate transactions.');
    } finally {
      setLoadingCandidates(false);
    }
  }, [report, toast]);

  const linkTransaction = useCallback(
    async (transactionId: string) => {
      try {
        await financePayrollService.linkTransaction(reportId, transactionId);
        setCandidates((prev) => prev.filter((c) => c.id !== transactionId));
        queryClient.invalidateQueries(FetchApiIds.FX_BULK_REPORTS);
      } catch {
        toast.showErrorFeedback('Failed to link transaction.');
      }
    },
    [reportId, queryClient, toast]
  );

  const unlinkTransaction = useCallback(
    async (transactionId: string) => {
      try {
        await financePayrollService.unlinkTransaction(reportId, transactionId);
        queryClient.invalidateQueries(FetchApiIds.FX_BULK_REPORTS);
      } catch {
        toast.showErrorFeedback('Failed to unlink transaction.');
      }
    },
    [reportId, queryClient, toast]
  );

  return { candidates, loadingCandidates, loadCandidates, linkTransaction, unlinkTransaction };
}
